import React from 'react';
import {
  Calendar,
  Clock,
  MapPin,
  Users,
  Globe,
  Tag,
  Target,
  BarChart3,
  Zap,
  Bell,
  MessageCircle,
  FileText,
  Settings,
  ArrowLeft,
  ChevronRight,
  Plus,
  Copy,
  Share2,
  Bookmark,
  CheckCircle2,
  Link2,
  Lightbulb,
  Play,
  Video,
  Download,
  ExternalLink,
  ThumbsUp,
  MessageSquare,
  Star,
  Award,
  Info,
  Sparkles,
  Send,
  MoreHorizontal,
  ChevronDown,
  User,
  Hash,
  Folder,
  BookOpen,
  Mic,
  Eye,
  Heart,
  Filter,
  Search,
  Layout,
} from 'lucide-react';
import './WorkshopsMockupContent.css';

const WorkshopsMockupContent = ({
  visibleItems = 0,
  animationPhase,
  activeInteriorTab = 'overview', // 'overview', 'schedule', 'announcements', 'resources', 'discussions', 'participants'
  onTabClick = () => {}, // Handler for tab clicks
}) => {
  // Workshop Data
  const workshopData = {
    title: 'NLP with Transformers: Practical Fine-tuning',
    status: 'upcoming',
    price: '$129.00',
    format: 'online',
    inviteCode: 'DB6693A32D1C0A3B',
    organizer: {
      name: 'Rehaan Ahmad',
      avatar: 'RA',
      role: 'Researcher',
    },
    details: {
      date: 'Feb 7, 2026 - Feb 8, 2026',
      time: '9:00 AM - 5:00 PM',
      location: 'Zoom',
      capacity: { current: 47, max: 80 },
      language: 'English',
      category: 'AI & ML',
      field: 'Computer Science',
      level: 'All Levels',
      startsIn: '7 days',
    },
    about: {
      description: 'A focused workshop on transformer architectures for NLP: from understanding attention mechanisms to deploying fine-tuned models in production.',
      topics: [
        'Transformer architecture deep dive',
        'Hugging Face ecosystem mastery',
        'Fine-tuning strategies (full, LoRA, adapters)',
        'Evaluation metrics and benchmarking',
        'Production deployment best practices',
      ],
    },
  };

  // Schedule Data
  const scheduleData = [
    {
      id: 1,
      day: 'Day 1',
      date: 'Feb 7, 2026',
      sessions: [
        { time: '9:00 AM', title: 'Welcome & Introduction', type: 'intro', duration: '30m' },
        { time: '9:30 AM', title: 'Transformer Architecture Deep Dive', type: 'lecture', duration: '90m' },
        { time: '11:00 AM', title: 'Coffee Break', type: 'break', duration: '15m' },
        { time: '11:15 AM', title: 'Hands-on: Building Your First Transformer', type: 'hands-on', duration: '105m' },
        { time: '1:00 PM', title: 'Lunch Break', type: 'break', duration: '60m' },
        { time: '2:00 PM', title: 'Hugging Face Ecosystem', type: 'lecture', duration: '60m' },
        { time: '3:00 PM', title: 'Lab: Fine-tuning with Transformers', type: 'hands-on', duration: '120m' },
      ],
    },
    {
      id: 2,
      day: 'Day 2',
      date: 'Feb 8, 2026',
      sessions: [
        { time: '9:00 AM', title: 'Advanced Fine-tuning Techniques', type: 'lecture', duration: '90m' },
        { time: '10:30 AM', title: 'Lab: LoRA and Adapter Methods', type: 'hands-on', duration: '90m' },
        { time: '12:00 PM', title: 'Lunch Break', type: 'break', duration: '60m' },
        { time: '1:00 PM', title: 'Evaluation & Benchmarking', type: 'lecture', duration: '60m' },
        { time: '2:00 PM', title: 'Production Deployment', type: 'lecture', duration: '60m' },
        { time: '3:00 PM', title: 'Final Project & Q&A', type: 'hands-on', duration: '120m' },
      ],
    },
  ];

  // Announcements Data
  const announcementsData = [
    {
      id: 1,
      emoji: '🔗',
      title: 'Useful Links & Community',
      content: 'Join our community channels:\n• Discord: discord.gg/ResearchPro-workshops\n• Slack: ResearchPro.slack.com/workshops\n• LinkedIn Group: linkedin.com/groups/ResearchPro',
      author: { name: 'Umer Ayub', avatar: 'UA' },
      date: 'Dec 18, 2025',
      priority: 'normal',
    },
    {
      id: 2,
      emoji: '💡',
      title: 'Tips for Getting the Most Out of This Workshop',
      content: 'Here are some tips to maximize your learning:\n1. Come prepared — Complete the pre-workshop setup\n2. Engage actively — Ask questions, participate\n3. Take notes — Document key learnings',
      author: { name: 'Rehaan Ahmad', avatar: 'RA' },
      date: 'Dec 15, 2025',
      priority: 'normal',
    },
    {
      id: 3,
      emoji: '⚡',
      title: 'Pre-Workshop Setup Required',
      content: 'Please complete the following before the workshop:\n• Install Python 3.9+\n• Set up a virtual environment\n• Install PyTorch and Transformers library',
      author: { name: 'Rehaan Ahmad', avatar: 'RA' },
      date: 'Dec 10, 2025',
      priority: 'important',
    },
    {
      id: 4,
      emoji: '🎉',
      title: 'Welcome to the Workshop!',
      content: "We're excited to have you join us for this deep dive into NLP with Transformers. Get ready for an intensive, hands-on learning experience!",
      author: { name: 'Rehaan Ahmad', avatar: 'RA' },
      date: 'Dec 1, 2025',
      priority: 'normal',
    },
  ];

  // Resources Data
  const resourcesData = [
    {
      id: 1,
      title: 'Workshop Slides - Day 1',
      type: 'slides',
      size: '15.2 MB',
      downloads: 234,
      date: 'Jan 28, 2026',
    },
    {
      id: 2,
      title: 'Starter Code Repository',
      type: 'code',
      size: '—',
      downloads: 189,
      date: 'Jan 25, 2026',
      isLink: true,
    },
    {
      id: 3,
      title: 'Transformer Architecture Cheatsheet',
      type: 'pdf',
      size: '2.4 MB',
      downloads: 312,
      date: 'Jan 20, 2026',
    },
    {
      id: 4,
      title: 'Dataset for Hands-on Labs',
      type: 'data',
      size: '45.8 MB',
      downloads: 156,
      date: 'Jan 18, 2026',
    },
    {
      id: 5,
      title: 'Recommended Reading List',
      type: 'link',
      size: '—',
      downloads: 98,
      date: 'Jan 15, 2026',
      isLink: true,
    },
  ];

  // Discussions Data
  const discussionsData = [
    {
      id: 1,
      title: 'Best GPU recommendations for fine-tuning?',
      author: { name: 'Tariq Hussain', avatar: 'TQ' },
      content: 'I\'m trying to decide between RTX 4090 and A100 for local fine-tuning. Any recommendations?',
      replies: 12,
      upvotes: 24,
      timeAgo: '2h ago',
      tags: ['hardware', 'gpu'],
      isHot: true,
    },
    {
      id: 2,
      title: 'LoRA vs Full Fine-tuning: When to use which?',
      author: { name: 'Nadia Kowalski', avatar: 'NK' },
      content: 'Can someone explain the trade-offs between LoRA and full fine-tuning approaches?',
      replies: 8,
      upvotes: 31,
      timeAgo: '5h ago',
      tags: ['fine-tuning', 'lora'],
      isAnswered: true,
    },
    {
      id: 3,
      title: 'Pre-workshop setup issues on M1 Mac',
      author: { name: 'Idris Malik', avatar: 'IM' },
      content: 'Getting MPS errors when running the starter code on M1 MacBook Pro.',
      replies: 5,
      upvotes: 8,
      timeAgo: '1d ago',
      tags: ['setup', 'macos'],
    },
    {
      id: 4,
      title: 'Will recordings be available after?',
      author: { name: 'Ingrid Svensson', avatar: 'IS' },
      content: 'Just wanted to confirm if workshop recordings will be shared with participants.',
      replies: 3,
      upvotes: 15,
      timeAgo: '2d ago',
      tags: ['general'],
      isAnswered: true,
    },
  ];

  // Participants Data
  const participantsData = [
    { id: 1, name: 'Rehaan Ahmad', avatar: 'RA', role: 'organizer', institution: 'MIT', status: 'online' },
    { id: 2, name: 'Umer Ayub', avatar: 'UA', role: 'co-organizer', institution: 'Stanford', status: 'online' },
    { id: 3, name: 'Tariq Hussain', avatar: 'TQ', role: 'participant', institution: 'Berkeley', status: 'online' },
    { id: 4, name: 'Nadia Kowalski', avatar: 'NK', role: 'participant', institution: 'CMU', status: 'offline' },
    { id: 5, name: 'Idris Malik', avatar: 'IM', role: 'participant', institution: 'Harvard', status: 'online' },
    { id: 6, name: 'Ingrid Svensson', avatar: 'IS', role: 'participant', institution: 'Oxford', status: 'offline' },
    { id: 7, name: 'Owen Gallagher', avatar: 'OG', role: 'participant', institution: 'Caltech', status: 'online' },
  ];

  // Get icon for resource type
  const getResourceIcon = (type) => {
    const icons = {
      slides: Layout,
      code: Hash,
      pdf: FileText,
      data: Folder,
      link: Link2,
      video: Video,
    };
    return icons[type] || FileText;
  };

  // Get session type styles
  const getSessionTypeClass = (type) => {
    const classes = {
      intro: 'session--intro',
      lecture: 'session--lecture',
      'hands-on': 'session--handson',
      break: 'session--break',
    };
    return classes[type] || '';
  };

  // Render Overview Tab
  const renderOverview = () => (
    <div className="ws-overview">
      {/* Invite Code Section */}
      <div className={`ws-invite-section ${visibleItems >= 1 ? 'visible' : ''}`}>
        <div className="ws-invite-header">
          <Share2 size={12} />
          <span>Invite Code</span>
        </div>
        <p className="ws-invite-subtitle">Share this code with participants:</p>
        <div className="ws-invite-code-box">
          <span className="ws-invite-code">{workshopData.inviteCode}</span>
          <button className="ws-copy-btn">
            <Copy size={12} />
          </button>
        </div>
        <button className="ws-share-btn">
          <Copy size={11} />
          <span>Copy Share Message</span>
        </button>
      </div>

      {/* Workshop Details Grid */}
      <div className={`ws-details-grid ${visibleItems >= 2 ? 'visible' : ''}`}>
        {/* Organizer Card */}
        <div className="ws-detail-card ws-detail-card--organizer">
          <div className="ws-organizer-avatar">
            <span>{workshopData.organizer.avatar}</span>
          </div>
          <div className="ws-organizer-info">
            <span className="ws-detail-label">ORGANIZER</span>
            <span className="ws-organizer-name">{workshopData.organizer.name}</span>
            <span className="ws-organizer-role">{workshopData.organizer.role}</span>
          </div>
          <ChevronRight size={14} className="ws-card-arrow" />
        </div>

        {/* Date Card */}
        <div className="ws-detail-card">
          <Calendar size={14} className="ws-detail-icon ws-detail-icon--blue" />
          <div className="ws-detail-content">
            <span className="ws-detail-label">DATE</span>
            <span className="ws-detail-value">{workshopData.details.date}</span>
          </div>
        </div>

        {/* Time Card */}
        <div className="ws-detail-card">
          <Clock size={14} className="ws-detail-icon ws-detail-icon--indigo" />
          <div className="ws-detail-content">
            <span className="ws-detail-label">TIME</span>
            <span className="ws-detail-value">{workshopData.details.time}</span>
          </div>
        </div>

        {/* Location Card */}
        <div className="ws-detail-card">
          <MapPin size={14} className="ws-detail-icon ws-detail-icon--green" />
          <div className="ws-detail-content">
            <span className="ws-detail-label">LOCATION</span>
            <span className="ws-detail-value">{workshopData.details.location}</span>
          </div>
        </div>

        {/* Capacity Card */}
        <div className="ws-detail-card">
          <Users size={14} className="ws-detail-icon ws-detail-icon--amber" />
          <div className="ws-detail-content">
            <span className="ws-detail-label">CAPACITY</span>
            <div className="ws-capacity-info">
              <span className="ws-capacity-count">
                <strong>{workshopData.details.capacity.current}</strong> / {workshopData.details.capacity.max}
              </span>
              <div className="ws-capacity-bar">
                <div 
                  className="ws-capacity-fill" 
                  style={{ width: `${(workshopData.details.capacity.current / workshopData.details.capacity.max) * 100}%` }}
                />
              </div>
            </div>
          </div>
        </div>

        {/* Language Card */}
        <div className="ws-detail-card">
          <Globe size={14} className="ws-detail-icon ws-detail-icon--cyan" />
          <div className="ws-detail-content">
            <span className="ws-detail-label">LANGUAGE</span>
            <span className="ws-detail-value">{workshopData.details.language}</span>
          </div>
        </div>

        {/* Category Card */}
        <div className="ws-detail-card">
          <Tag size={14} className="ws-detail-icon ws-detail-icon--pink" />
          <div className="ws-detail-content">
            <span className="ws-detail-label">CATEGORY</span>
            <span className="ws-detail-value">{workshopData.details.category}</span>
          </div>
        </div>

        {/* Field Card */}
        <div className="ws-detail-card">
          <Target size={14} className="ws-detail-icon ws-detail-icon--indigo" />
          <div className="ws-detail-content">
            <span className="ws-detail-label">FIELD</span>
            <span className="ws-detail-value">{workshopData.details.field}</span>
          </div>
        </div>

        {/* Level Card */}
        <div className="ws-detail-card">
          <BarChart3 size={14} className="ws-detail-icon ws-detail-icon--green" />
          <div className="ws-detail-content">
            <span className="ws-detail-label">LEVEL</span>
            <span className="ws-level-badge">
              <Users size={9} />
              {workshopData.details.level}
            </span>
          </div>
        </div>

        {/* Starts In Card */}
        <div className="ws-detail-card ws-detail-card--countdown">
          <Zap size={14} className="ws-detail-icon ws-detail-icon--amber" />
          <div className="ws-detail-content">
            <span className="ws-detail-label">STARTS IN</span>
            <span className="ws-countdown">{workshopData.details.startsIn}</span>
          </div>
        </div>
      </div>

      {/* About Section */}
      <div className={`ws-about-section ${visibleItems >= 3 ? 'visible' : ''}`}>
        <div className="ws-about-header">
          <Info size={12} />
          <span>About this Workshop</span>
        </div>
        <p className="ws-about-description">{workshopData.about.description}</p>
        <div className="ws-about-topics">
          <span className="ws-topics-label">**What You'll Learn:**</span>
          <ul className="ws-topics-list">
            {workshopData.about.topics.map((topic, idx) => (
              <li key={idx} className="ws-topic-item">
                <CheckCircle2 size={10} />
                <span>{topic}</span>
              </li>
            ))}
          </ul>
        </div>
      </div>
    </div>
  );

  // Render Schedule Tab
  const renderSchedule = () => (
    <div className="ws-schedule">
      <div className="ws-schedule-header">
        <div className="ws-schedule-title-row">
          <Calendar size={14} />
          <h3>Workshop Schedule</h3>
        </div>
        <span className="ws-schedule-badge">2 Days</span>
      </div>

      <div className="ws-schedule-days">
        {scheduleData.map((day, dayIdx) => (
          <div 
            key={day.id} 
            className={`ws-schedule-day ${dayIdx < visibleItems ? 'visible' : ''}`}
            style={{ transitionDelay: `${dayIdx * 100}ms` }}
          >
            <div className="ws-day-header">
              <span className="ws-day-label">{day.day}</span>
              <span className="ws-day-date">{day.date}</span>
            </div>
            <div className="ws-sessions">
              {day.sessions.map((session, idx) => (
                <div 
                  key={idx} 
                  className={`ws-session ${getSessionTypeClass(session.type)}`}
                >
                  <span className="ws-session-time">{session.time}</span>
                  <div className="ws-session-content">
                    <span className="ws-session-title">{session.title}</span>
                    <div className="ws-session-meta">
                      <span className="ws-session-type">{session.type}</span>
                      <span className="ws-session-duration">
                        <Clock size={9} />
                        {session.duration}
                      </span>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        ))}
      </div>
    </div>
  );

  // Render Announcements Tab
  const renderAnnouncements = () => (
    <div className="ws-announcements">
      <div className="ws-announcements-header">
        <div className="ws-announcements-title-row">
          <Bell size={14} />
          <div>
            <h3>Announcements</h3>
            <span className="ws-announcements-count">{announcementsData.length} announcements</span>
          </div>
        </div>
        <button className="ws-new-announcement-btn">
          <Plus size={11} />
          <span>New Announcement</span>
        </button>
      </div>

      <div className="ws-announcements-list">
        {announcementsData.map((announcement, idx) => (
          <div 
            key={announcement.id} 
            className={`ws-announcement-card ${announcement.priority === 'important' ? 'ws-announcement-card--important' : ''} ${idx < visibleItems ? 'visible' : ''}`}
            style={{ transitionDelay: `${idx * 80}ms` }}
          >
            <div className="ws-announcement-priority-bar" />
            <div className="ws-announcement-body">
              <div className="ws-announcement-header">
                <span className="ws-announcement-emoji">{announcement.emoji}</span>
                <h4 className="ws-announcement-title">{announcement.title}</h4>
                <span className={`ws-priority-badge ws-priority-badge--${announcement.priority}`}>
                  {announcement.priority.toUpperCase()}
                </span>
              </div>
              <p className="ws-announcement-content">
                {announcement.content.split('\n').slice(0, 2).join(' ')}...
              </p>
              <span className="ws-read-more">Read more</span>
              <div className="ws-announcement-footer">
                <div className="ws-announcement-author">
                  <div className="ws-author-avatar">
                    <span>{announcement.author.avatar}</span>
                  </div>
                  <span className="ws-author-name">{announcement.author.name}</span>
                </div>
                <span className="ws-announcement-date">{announcement.date}</span>
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );

  // Render Resources Tab
  const renderResources = () => (
    <div className="ws-resources">
      <div className="ws-resources-header">
        <div className="ws-resources-title-row">
          <Folder size={14} />
          <div>
            <h3>Resources</h3>
            <span className="ws-resources-count">{resourcesData.length} files available</span>
          </div>
        </div>
        <button className="ws-upload-btn">
          <Plus size={11} />
          <span>Upload</span>
        </button>
      </div>

      <div className="ws-resources-list">
        {resourcesData.map((resource, idx) => {
          const IconComponent = getResourceIcon(resource.type);
          return (
            <div 
              key={resource.id} 
              className={`ws-resource-card ${idx < visibleItems ? 'visible' : ''}`}
              style={{ transitionDelay: `${idx * 80}ms` }}
            >
              <div className={`ws-resource-icon ws-resource-icon--${resource.type}`}>
                <IconComponent size={14} />
              </div>
              <div className="ws-resource-info">
                <h4 className="ws-resource-title">{resource.title}</h4>
                <div className="ws-resource-meta">
                  <span className="ws-resource-size">{resource.size}</span>
                  <span className="ws-resource-dot">•</span>
                  <span className="ws-resource-downloads">
                    <Download size={9} />
                    {resource.downloads}
                  </span>
                  <span className="ws-resource-dot">•</span>
                  <span className="ws-resource-date">{resource.date}</span>
                </div>
              </div>
              <button className="ws-resource-action">
                {resource.isLink ? <ExternalLink size={12} /> : <Download size={12} />}
              </button>
            </div>
          );
        })}
      </div>
    </div>
  );

  // Render Discussions Tab
  const renderDiscussions = () => (
    <div className="ws-discussions">
      <div className="ws-discussions-header">
        <div className="ws-discussions-title-row">
          <MessageCircle size={14} />
          <div>
            <h3>Discussions</h3>
            <span className="ws-discussions-count">{discussionsData.length} threads</span>
          </div>
        </div>
        <button className="ws-new-discussion-btn">
          <Plus size={11} />
          <span>New Thread</span>
        </button>
      </div>

      <div className="ws-discussions-list">
        {discussionsData.map((discussion, idx) => (
          <div 
            key={discussion.id} 
            className={`ws-discussion-card ${idx < visibleItems ? 'visible' : ''}`}
            style={{ transitionDelay: `${idx * 80}ms` }}
          >
            <div className="ws-discussion-author-avatar">
              <span>{discussion.author.avatar}</span>
            </div>
            <div className="ws-discussion-content">
              <div className="ws-discussion-header">
                <h4 className="ws-discussion-title">{discussion.title}</h4>
                <div className="ws-discussion-badges">
                  {discussion.isHot && (
                    <span className="ws-discussion-badge ws-discussion-badge--hot">
                      <Zap size={8} />
                      Hot
                    </span>
                  )}
                  {discussion.isAnswered && (
                    <span className="ws-discussion-badge ws-discussion-badge--answered">
                      <CheckCircle2 size={8} />
                      Answered
                    </span>
                  )}
                </div>
              </div>
              <p className="ws-discussion-preview">{discussion.content}</p>
              <div className="ws-discussion-tags">
                {discussion.tags.map((tag, tagIdx) => (
                  <span key={tagIdx} className="ws-discussion-tag">#{tag}</span>
                ))}
              </div>
              <div className="ws-discussion-footer">
                <span className="ws-discussion-author">{discussion.author.name}</span>
                <span className="ws-discussion-dot">•</span>
                <span className="ws-discussion-time">{discussion.timeAgo}</span>
                <div className="ws-discussion-stats">
                  <span className="ws-discussion-stat">
                    <ThumbsUp size={10} />
                    {discussion.upvotes}
                  </span>
                  <span className="ws-discussion-stat">
                    <MessageSquare size={10} />
                    {discussion.replies}
                  </span>
                </div>
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );

  // Render Participants Tab
  const renderParticipants = () => (
    <div className="ws-participants">
      <div className="ws-participants-header">
        <div className="ws-participants-title-row">
          <Users size={14} />
          <div>
            <h3>Participants</h3>
            <span className="ws-participants-count">{participantsData.length} members</span>
          </div>
        </div>
        <div className="ws-participants-search">
          <Search size={11} />
          <input type="text" placeholder="Search..." readOnly />
        </div>
      </div>

      <div className="ws-participants-list">
        {participantsData.map((participant, idx) => (
          <div 
            key={participant.id} 
            className={`ws-participant-card ${idx < visibleItems ? 'visible' : ''}`}
            style={{ transitionDelay: `${idx * 60}ms` }}
          >
            <div className={`ws-participant-avatar ws-participant-avatar--${participant.role}`}>
              <span>{participant.avatar}</span>
              <span className={`ws-participant-status ws-participant-status--${participant.status}`} />
            </div>
            <div className="ws-participant-info">
              <div className="ws-participant-name-row">
                <span className="ws-participant-name">{participant.name}</span>
                {participant.role !== 'participant' && (
                  <span className={`ws-participant-role-badge ws-participant-role-badge--${participant.role}`}>
                    {participant.role === 'organizer' ? 'Organizer' : 'Co-organizer'}
                  </span>
                )}
              </div>
              <span className="ws-participant-institution">{participant.institution}</span>
            </div>
            <button className="ws-participant-action">
              <MessageCircle size={12} />
            </button>
          </div>
        ))}
      </div>
    </div>
  );

  // Tab configuration
  const tabs = [
    { key: 'overview', icon: Layout, label: 'Overview' },
    { key: 'schedule', icon: Calendar, label: 'Schedule' },
    { key: 'announcements', icon: Bell, label: 'Announcements' },
    { key: 'resources', icon: Folder, label: 'Resources' },
    { key: 'discussions', icon: MessageCircle, label: 'Discussions' },
    { key: 'participants', icon: Users, label: 'Participants' },
  ];

  // Render content based on active tab
  const renderContent = () => {
    switch (activeInteriorTab) {
      case 'schedule':
        return renderSchedule();
      case 'announcements':
        return renderAnnouncements();
      case 'resources':
        return renderResources();
      case 'discussions':
        return renderDiscussions();
      case 'participants':
        return renderParticipants();
      default:
        return renderOverview();
    }
  };

  return (
    <div className="ws-mockup-content">
      {/* Workshop Header */}
      <div className="ws-mock-header">
        <button className="ws-back-btn">
          <ArrowLeft size={14} />
        </button>
        <div className="ws-header-info">
          <h2 className="ws-title">{workshopData.title}</h2>
        </div>
        <div className="ws-header-badges">
          <span className="ws-status-badge ws-status-badge--soon">
            <Sparkles size={9} />
            SOON
          </span>
          <span className="ws-price-badge">{workshopData.price}</span>
          <span className="ws-format-badge">
            <Video size={9} />
            ONLINE
          </span>
        </div>
        <div className="ws-header-actions">
          <button className="ws-action-icon">
            <Bookmark size={14} />
          </button>
          <button className="ws-action-icon">
            <Share2 size={14} />
          </button>
        </div>
      </div>

      {/* Workshop Tabs */}
      <div className="ws-tabs">
        {tabs.map((tab) => (
          <button
            key={tab.key}
            className={`ws-tab ${activeInteriorTab === tab.key ? 'ws-tab--active' : ''}`}
            onClick={() => onTabClick(tab.key)}
          >
            <tab.icon size={11} />
            <span>{tab.label}</span>
          </button>
        ))}
        <button className="ws-tab ws-tab--settings">
          <Settings size={11} />
          <span>Settings</span>
        </button>
      </div>

      {/* Tab Content */}
      <div className="ws-content">
        {renderContent()}
      </div>
    </div>
  );
};

export default WorkshopsMockupContent;
